import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  InputBase,
  IconButton,
  useTheme,
  Button,
  Avatar,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { MessageCircle, ArrowUp, Video, RotateCcw } from 'lucide-mui';
import { styled, alpha } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import newLogo from '../../../../assets/icons/new_logo.svg';
import TimestampChip from '../../../common/TimestampChip';
import meetingImage from './assets/meeting.webp';

interface MockMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

interface MockSuggestion {
  title: string;
  prompt: string;
}

const mockSuggestions: MockSuggestion[] = [
  {
    title: 'What are the main points?',
    prompt: 'What are the main points discussed in this video?',
  },
  {
    title: 'Key takeaways',
    prompt: 'What are the key takeaways from this conversation?',
  },
  {
    title: 'Action items',
    prompt: 'What action items were mentioned?',
  },
];

const mockConversation: MockMessage[] = [
  {
    id: '1',
    role: 'user',
    content: 'What are the main points discussed in this video?',
  },
  {
    id: '2',
    role: 'assistant',
    content:
      'Based on the video content, here are the main points:\n\n1. **Project Introduction** - Julian outlines the new React project goals at [00:35]\n2. **Technical Architecture** - Discussion of microservices and API design patterns\n3. **Timeline Planning** - Sprint planning and Q4 deadline concerns\n\nWould you like me to find the exact timestamps for any of these topics?',
  },
];

// Styled components matching the real chat
const MessageContainer = styled(Box)<{ role?: string }>(({ theme, role }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 0.5),
  flexDirection: role === 'user' ? 'row-reverse' : 'row',
}));

const MessageBubble = styled(Box)<{ role?: string }>(({ theme, role }) => ({
  position: 'relative',
  padding: theme.spacing(1.5, 2),
  borderRadius: '12px',
  maxWidth: '90%',
  width: 'fit-content',
  marginBottom: theme.spacing(1.5),
  transition: 'all 0.2s ease',
  animation: 'fadeIn 0.3s ease-out',
  fontSize: '0.875rem',
  lineHeight: 1.5,
  letterSpacing: 'normal',
  ...(role === 'user'
    ? {
        marginLeft: 'auto',
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderTopRightRadius: '4px',
        '& p': {
          color: `${theme.palette.primary.contrastText} !important`,
          margin: 0,
          '&:not(:last-child)': {
            marginBottom: '0.75rem',
          },
        },
      }
    : {
        marginRight: 'auto',
        backgroundColor: theme.palette.background.default,
        borderTopLeftRadius: '4px',
        '& p': {
          margin: 0,
          '&:not(:last-child)': {
            marginBottom: '0.75rem',
          },
        },
        '& code': {
          backgroundColor: alpha(theme.palette.secondary.main, 0.08),
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '0.8125rem',
        },
      }),
  '@keyframes fadeIn': {
    from: {
      opacity: 0,
      transform: 'translateY(10px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

const AskAIDemo: React.FC = () => {
  const theme = useTheme();
  const [messages, setMessages] = useState<MockMessage[]>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [isVideoMode, setIsVideoMode] = useState(false);
  const [demoStep, setDemoStep] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [showVideoAnalyzing, setShowVideoAnalyzing] = useState(false);
  const [typingIndex, setTypingIndex] = useState(0);
  const chatRef = useRef<HTMLDivElement>(null);

  const fullTypingText = 'What are the main points discussed in this video?';
  const secondTypingText = 'Analyze the visual content of this video';

  // Auto-scroll to bottom function
  const scrollToBottom = () => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  };

  // Auto-scroll when messages, typing, or video analyzing changes
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, showVideoAnalyzing, currentInput]);

  // Demo animation sequence
  useEffect(() => {
    let delay: number;
    if (demoStep === 0) {
      delay = 100; // Start immediately instead of 2000ms
    } else if (demoStep === 1) {
      delay = 50; // Fast typing speed
    } else if (demoStep === 4) {
      delay = 50; // Fast typing speed for second input
    } else if (demoStep === 5) {
      delay = 3000;
    } else {
      delay = 1800;
    }

    const timer = setTimeout(() => {
      switch (demoStep) {
        case 0:
          // Initial state - show suggestions
          setShowSuggestions(true);
          setDemoStep(1);
          break;
        case 1:
          // Simulate typing character by character
          if (typingIndex < fullTypingText.length) {
            setCurrentInput(fullTypingText.slice(0, typingIndex + 1));
            setTypingIndex(typingIndex + 1);
            // Don't advance demoStep, stay in case 1 until typing is complete
          } else {
            // Typing complete, move to next step
            setDemoStep(2);
          }
          break;
        case 2:
          // Submit the question
          setMessages([mockConversation[0]]);
          setCurrentInput('');
          setTypingIndex(0);
          setShowSuggestions(false);
          setIsTyping(true);
          setDemoStep(3);
          break;
        case 3:
          // Show AI response
          setMessages(mockConversation);
          setIsTyping(false);
          setDemoStep(4);
          break;
        case 4:
          // Toggle video mode and start typing second message
          if (typingIndex === 0) {
            // First time in step 4 - enable video mode and start typing
            setIsVideoMode(true);
            setTypingIndex(1); // Start typing
          }

          // Stream typing for second message
          if (typingIndex <= secondTypingText.length) {
            setCurrentInput(secondTypingText.slice(0, typingIndex));
            setTypingIndex(typingIndex + 1);
            // Don't advance demoStep, stay in case 4 until typing is complete
          } else {
            // Typing complete, show video analyzing and move to next step
            setShowVideoAnalyzing(true);
            setDemoStep(5);
          }
          break;
        case 5:
          // Hide video analyzing and show result
          setShowVideoAnalyzing(false);
          setMessages([
            ...mockConversation,
            {
              id: '3',
              role: 'user',
              content: 'Analyze the visual content of this video',
            },
            {
              id: '4',
              role: 'assistant',
              content:
                "Based on my visual analysis of the video frames, here's what I observed:\n\n## **Visual Elements Detected**\n• **Meeting Environment**: Professional conference room with glass walls and modern furniture\n• **Presentation Setup**: Large wall-mounted display showing technical architecture diagrams\n• **Participants**: 4 people visible - 3 seated around table, 1 standing presenting\n• **Documentation**: Laptops, notebooks, and papers scattered on conference table\n\n## **Screen Content Analysis**\n• **Slide 1 [02:15]**: React component hierarchy diagram with state management flow\n• **Slide 2 [04:30]**: Database schema showing user tables and relationships\n• **Slide 3 [06:45]**: API endpoint documentation with request/response examples\n\n## **Body Language & Engagement**\n• High engagement level - active note-taking and nodding\n• Presenter using hand gestures to emphasize technical points\n• Team members leaning forward during architecture discussion\n\n## **Key Visual Moments**\n• [01:25] - Julian points to whiteboard with initial project sketches\n• [05:12] - Sarah demonstrates API calls on her laptop screen\n• [08:30] - Mike highlights timeline concerns with calendar app\n\nWould you like me to extract text from any specific slides or analyze particular moments in more detail?",
            },
          ]);
          setCurrentInput('');
          setDemoStep(6);
          break;
        case 6:
          // Reset and loop
          setMessages([]);
          setCurrentInput('');
          setShowSuggestions(true);
          setIsVideoMode(false);
          setIsTyping(false);
          setShowVideoAnalyzing(false);
          setDemoStep(0);
          break;
        default:
          break;
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [demoStep, typingIndex]);

  const handleSuggestionClick = (suggestion: MockSuggestion) => {
    setCurrentInput(suggestion.prompt);
  };

  const getAvatar = (role: string) => {
    if (role === 'user') {
      return (
        <Avatar
          sx={{
            width: 28,
            height: 28,
            backgroundColor: theme.palette.primary.main,
            fontSize: '0.75rem',
            fontWeight: 600,
            marginTop: 0.5,
          }}
        >
          U
        </Avatar>
      );
    }

    return (
      <Avatar
        src={newLogo}
        sx={{
          width: 28,
          height: 28,
          marginTop: 0.5,
        }}
      />
    );
  };

  const processTimestamps = (text: string) => {
    const timestampRegex = /\[(\d{2}:\d{2})\]/g;
    const parts = text.split(timestampRegex);

    return parts.map((part, index) => {
      if (part.match(/^\d{2}:\d{2}$/)) {
        return (
          <TimestampChip
            key={index}
            timestamp={part}
            onClick={() => {}}
            sx={{ mx: 0.5 }}
          />
        );
      }
      return part;
    });
  };

  const renderMessage = (message: MockMessage) => (
    <MessageContainer role={message.role} key={message.id}>
      {getAvatar(message.role)}
      <MessageBubble role={message.role}>
        <Box sx={{ pr: message.role === 'assistant' ? 4 : 0 }}>
          {message.role === 'assistant' ? (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => (
                  <Typography
                    variant="body2"
                    component="p"
                    sx={{
                      wordBreak: 'break-word',
                      margin: 0,
                      '&:not(:last-child)': {
                        marginBottom: '0.75rem',
                      },
                    }}
                  >
                    {processTimestamps(children?.toString() || '')}
                  </Typography>
                ),
                ul: ({ children }) => (
                  <Box
                    component="ul"
                    sx={{ margin: '0.5rem 0', paddingLeft: '1.25rem' }}
                  >
                    {children}
                  </Box>
                ),
                ol: ({ children }) => (
                  <Box
                    component="ol"
                    sx={{ margin: '0.5rem 0', paddingLeft: '1.25rem' }}
                  >
                    {children}
                  </Box>
                ),
                li: ({ children }) => {
                  // Extract text content from React children
                  const getTextContent = (node: any): string => {
                    if (typeof node === 'string') return node;
                    if (Array.isArray(node))
                      return node.map(getTextContent).join('');
                    if (node?.props?.children)
                      return getTextContent(node.props.children);
                    return '';
                  };

                  const textContent = getTextContent(children);

                  return (
                    <Typography
                      component="li"
                      variant="body2"
                      sx={{
                        wordBreak: 'break-word',
                        marginBottom: '0.25rem',
                      }}
                    >
                      {processTimestamps(textContent)}
                    </Typography>
                  );
                },
                strong: ({ children }) => (
                  <Typography
                    component="strong"
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      display: 'inline',
                    }}
                  >
                    {children}
                  </Typography>
                ),
                code: ({ children }) => (
                  <Typography
                    component="code"
                    variant="body2"
                    sx={{
                      backgroundColor: alpha(
                        theme.palette.secondary.main,
                        0.08
                      ),
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '0.8125rem',
                      fontFamily: 'monospace',
                      display: 'inline',
                    }}
                  >
                    {children}
                  </Typography>
                ),
              }}
            >
              {message.content}
            </ReactMarkdown>
          ) : (
            <Typography
              variant="body2"
              sx={{
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
              }}
            >
              {message.content}
            </Typography>
          )}
        </Box>
      </MessageBubble>
    </MessageContainer>
  );

  // Video analyzing component
  const renderVideoAnalyzing = () => (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: '1rem',
      }}
    >
      <Avatar
        src={newLogo}
        sx={{
          width: 28,
          height: 28,
          marginTop: 0.5,
          marginRight: '0.5rem',
        }}
      />
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          borderRadius: '12px',
          borderTopLeftRadius: '4px',
          position: 'relative',
          maxWidth: '70%',
          minWidth: '300px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            paddingTop: '56.25%', // 16:9 aspect ratio
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(0,0,0,0.05)',
          }}
        >
          <Box
            component="img"
            src={meetingImage}
            alt="Meeting video frame"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(
                to bottom,
                ${theme.palette.background.paper}00,
                ${theme.palette.background.paper}66,
                ${theme.palette.background.paper}00
              )`,
              animation: 'scan 2s ease-in-out infinite',
              '@keyframes scan': {
                '0%': {
                  transform: 'translateY(-100%)',
                },
                '100%': {
                  transform: 'translateY(100%)',
                },
              },
            }}
          />
        </Box>

        <Box
          sx={{
            padding: theme.spacing(1.5, 2),
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Box
            component="span"
            sx={{
              color: theme.palette.text.primary,
              fontSize: '0.875rem',
            }}
          >
            Analyzing visual content...
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            {[0, 1, 2].map((index) => (
              <Box
                key={index}
                component="span"
                sx={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: theme.palette.primary.main,
                  opacity: 0.7,
                  animation: 'fadeInOut 1.5s infinite',
                  animationDelay: `${index * 0.2}s`,
                  '@keyframes fadeInOut': {
                    '0%, 100%': { opacity: 0.3 },
                    '50%': { opacity: 0.7 },
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        p: 3,
        minHeight: 450,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <MessageCircle sx={{ fontSize: 20 }} />
          Ask Questions
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Chat about your content and get instant answers
        </Typography>
      </Box>

      {/* Chat Messages */}
      <Box
        ref={chatRef}
        sx={{
          flex: 1,
          mb: 2,
          minHeight: 200,
          maxHeight: 400,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(0,0,0,0.3)',
          },
        }}
      >
        {messages.length === 0 && showSuggestions ? (
          // Empty state with suggestions - using actual Suggestions component styling
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, textAlign: 'center' }}
            >
              Try asking one of these questions:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {mockSuggestions.map((suggestion, index) => (
                <Button
                  key={index}
                  variant="text"
                  onClick={() => handleSuggestionClick(suggestion)}
                  fullWidth
                  sx={{
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.background.default,
                    borderRadius: '20px',
                    textTransform: 'none',
                    p: 0,
                    minHeight: 32,
                    display: 'block',
                    textAlign: 'left',
                    transition: 'all 0.15s ease-in-out',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                    mb: 1,
                  }}
                >
                  <Box sx={{ px: 1.5, py: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 400,
                      }}
                    >
                      {suggestion.title}
                    </Typography>
                  </Box>
                </Button>
              ))}
            </Box>
          </Box>
        ) : (
          // Chat conversation
          <Box sx={{ mt: 2 }}>
            {messages.map((message) => renderMessage(message))}

            {/* Video analyzing indicator */}
            {showVideoAnalyzing && renderVideoAnalyzing()}

            {/* Typing indicator */}
            {isTyping && (
              <MessageContainer>
                {getAvatar('assistant')}
                <MessageBubble>
                  <Typography variant="body2" color="text.secondary">
                    AI is thinking...
                  </Typography>
                </MessageBubble>
              </MessageContainer>
            )}
          </Box>
        )}
      </Box>

      {/* Input Area - exact styling from Ask.tsx */}
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Paper
          component="form"
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            boxShadow: 'none',
            border: 'none',
            borderRadius: 6,
            backgroundColor: theme.palette.background.default,
            position: 'relative',
          }}
        >
          {/* Video mode toggle - exact styling */}
          <Box>
            <Tooltip
              title={
                isVideoMode ? 'Video analyzer active' : 'Enable video analyzer'
              }
              placement="top"
              arrow
            >
              <IconButton
                data-video-analyzer-button
                sx={{
                  ml: 1,
                  backgroundColor: isVideoMode
                    ? theme.palette.primary.main
                    : theme.palette.background.paper,
                  borderRadius: '9999px',
                  width: '40px',
                  height: '32px',
                  padding: '8px',
                  transition: 'all 0.2s ease-in-out',
                  border: `2px solid ${
                    isVideoMode
                      ? theme.palette.primary.main
                      : theme.palette.divider
                  }`,
                  boxShadow: isVideoMode
                    ? 'none'
                    : `inset 0 0 0 1px ${theme.palette.divider}`,
                  position: 'absolute',
                  left: 0,
                  bottom: '4px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    left: isVideoMode ? '12px' : '2px',
                    top: '2px',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: isVideoMode
                      ? theme.palette.primary.contrastText
                      : theme.palette.action.active,
                    transition: 'all 0.2s ease-in-out',
                    opacity: 0.1,
                  },
                  '&:hover': {
                    backgroundColor: isVideoMode
                      ? theme.palette.primary.dark
                      : theme.palette.background.paper,
                    borderColor: isVideoMode
                      ? theme.palette.primary.dark
                      : theme.palette.primary.main,
                  },
                  '& svg': {
                    transition: 'all 0.2s ease-in-out',
                    transform: isVideoMode
                      ? 'translateX(6px)'
                      : 'translateX(-6px)',
                    position: 'relative',
                    zIndex: 1,
                  },
                }}
              >
                <Video
                  style={{
                    color: isVideoMode
                      ? theme.palette.primary.contrastText
                      : theme.palette.text.primary,
                    width: '16px',
                    height: '16px',
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>

          <InputBase
            sx={{
              ml: 6.5,
              flex: 1,
              height: 'auto',
              minHeight: '2.5rem',
              paddingRight: '40px',
              paddingLeft: '2px',
            }}
            placeholder={
              isVideoMode
                ? 'Analyze visual content'
                : 'Ask anything about this video'
            }
            inputProps={{ 'aria-label': 'ask ai', multiline: true }}
            value={currentInput}
            multiline
            readOnly
          />

          {/* Send/Clear button */}
          {(() => {
            if (currentInput) {
              return (
                <IconButton
                  type="submit"
                  sx={{
                    position: 'absolute',
                    bottom: '6px',
                    right: '6px',
                    zIndex: 1,
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    padding: '6px',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.light,
                      '& .MuiSvgIcon-root': {
                        transform: 'translateY(-2px)',
                      },
                    },
                  }}
                >
                  <ArrowUp
                    style={{
                      transition: 'transform 0.2s ease-in-out',
                      color: theme.palette.primary.contrastText,
                      width: '16px',
                      height: '16px',
                    }}
                  />
                </IconButton>
              );
            }

            if (messages.length > 0) {
              return (
                <IconButton
                  sx={{
                    position: 'absolute',
                    bottom: '6px',
                    right: '6px',
                    zIndex: 1,
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    padding: '6px',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                      '& svg': {
                        transform: 'translateY(-2px)',
                      },
                    },
                  }}
                >
                  <RotateCcw
                    style={{
                      color: theme.palette.text.secondary,
                      transition: 'transform 0.2s ease-in-out',
                      width: '16px',
                      height: '16px',
                    }}
                  />
                </IconButton>
              );
            }

            return null;
          })()}

          {isTyping && (
            <CircularProgress
              size="1rem"
              sx={{
                color: theme.palette.primary.main,
                position: 'absolute',
                bottom: '12px',
                right: '12px',
              }}
            />
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default AskAIDemo;
